// ============================================================
// Sidebar — desktop collapsible left navigation.
// ------------------------------------------------------------
// Implements the DI-Navigation Menu design:
//   • 280px expanded → 72px icon-only collapsed
//   • Brand header: app logo + wordmark (clickable → dashboard)
//   • Role-specific nav items with active state (primary bg)
//   • Badges (unread alerts, order counts)
//   • Role stats widgets (earnings, rating, revenue, wallet)
//   • Profile + Logout in the footer
//
// All navigation is role-aware via getModuleRoute() so the
// Dashboard item always lands on the correct role-scoped page
// (admin→#/admin, merchant→#/merchant, customer→#/app).
//
// Collapse state persists in localStorage.
// ============================================================

import { useState, useEffect } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Settings,
  HelpCircle,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  DollarSign,
  TrendingUp,
  Star,
  Package,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import {
  MODULES,
  getSidebarModules,
  getActiveModule,
  getRoleStats,
  getModuleRoute,
  moduleLabel,
  type NavModule,
} from "@/lib/navigation";
import { Logo } from "@/components/brand/logo";
import { BrandWordmark } from "@/components/brand/wordmark";

const COLLAPSE_KEY = "horndeliver.sidebar.collapsed";

// ============================================================
// SidebarContent — the inner content shared by desktop and mobile.
// ============================================================
function SidebarContent({
  collapsed,
  onNavigate,
  onToggleCollapse,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  onToggleCollapse?: () => void;
}) {
  const { user, logout } = useAuthStore();
  const route = useUiStore((s) => s.route);
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();

  // Fetch notifications for the badge count.
  const { data: notifData } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.notifications.list(),
    refetchInterval: 30000,
  });
  const unread = notifData?.data?.notifications?.filter((n) => !n.read).length ?? 0;

  if (!user) return null;

  const sidebarModules = getSidebarModules(user);
  const active = getActiveModule(route.path);
  const stats = getRoleStats(user);

  function getBadge(id: string): number | null {
    if (id === "alerts") return unread > 0 ? unread : null;
    return null;
  }

  const go = (m: NavModule) => {
    navigate(getModuleRoute(user, m));
    onNavigate?.();
  };

  const handleLogout = async () => {
    onNavigate?.();
    toast.info(t("profile.signingOut") !== "profile.signingOut" ? t("profile.signingOut") : "Signing out...");
    await logout();
  };

  return (
    <div className="flex h-full flex-col">
      {/* ---- Brand header — app logo + wordmark (clickable → dashboard) ---- */}
      <div className={`border-b border-border/60 px-4 py-3 ${collapsed ? "flex justify-center" : ""}`}>
        <button
          onClick={() => go(MODULES.dashboard)}
          className="flex items-center gap-2"
          aria-label={t("nav.homeAria")}
        >
          <Logo className="h-7 w-7 shrink-0" />
          {!collapsed && <BrandWordmark className="text-base" />}
        </button>
      </div>

      {/* ---- Nav items ---- */}
      <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label={t("nav.sidebarAria")}>
        <div className="space-y-0.5">
          {sidebarModules.map((m) => {
            const isActive = active?.id === m.id || (m.id === "dashboard" && !active);
            const badge = getBadge(m.id);
            return (
              <SidebarItem
                key={m.id}
                module={m}
                active={isActive}
                collapsed={collapsed}
                badge={badge}
                onClick={() => go(m)}
                label={moduleLabel(m, t)}
              />
            );
          })}
        </div>
      </nav>

      {/* ---- Stats widgets ---- */}
      {!collapsed && stats.length > 0 && (
        <div className="border-t border-border/60 px-3 py-3">
          <div className="space-y-1.5">
            {stats.map((stat) => (
              <StatRow key={stat.id} stat={stat} user={user} />
            ))}
          </div>
        </div>
      )}

      {/* ---- Footer: Profile, Settings, Help, Logout, Collapse ---- */}
      <div className="border-t border-border/60 px-2 py-2">
        <SidebarItem
          module={MODULES.profile}
          active={active?.id === "profile"}
          collapsed={collapsed}
          onClick={() => go(MODULES.profile)}
          label={moduleLabel(MODULES.profile, t)}
        />
        {collapsed ? (
          <>
            <SidebarIconOnly icon={Settings} label={t("nav.settings")} onClick={() => go(MODULES.settings)} />
            <SidebarIconOnly icon={HelpCircle} label={t("nav.help")} onClick={() => go(MODULES.help)} />
            <SidebarIconOnly icon={LogOut} label={t("profile.signOut")} onClick={handleLogout} danger />
            <SidebarIconOnly icon={collapsed ? PanelLeft : PanelLeftClose} label={t("nav.expand")} onClick={() => onToggleCollapse?.()} />
          </>
        ) : (
          <>
            <SidebarItem
              module={MODULES.settings}
              active={active?.id === "settings"}
              collapsed={false}
              onClick={() => go(MODULES.settings)}
              label={moduleLabel(MODULES.settings, t)}
            />
            <SidebarItem
              module={MODULES.help}
              active={active?.id === "help"}
              collapsed={false}
              onClick={() => go(MODULES.help)}
              label={moduleLabel(MODULES.help, t)}
            />
            {/* Logout — restored. Sits above Collapse so nothing overlaps. */}
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-destructive transition hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate text-left">{t("profile.signOut")}</span>
            </button>
            {/* Collapse toggle — inline at the bottom (was absolute, caused overlap). */}
            <button
              onClick={() => onToggleCollapse?.()}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
              {!collapsed && <span>{t("nav.collapse")}</span>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Sidebar — desktop sticky sidebar with collapse toggle.
// ============================================================
export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_KEY);
      if (saved !== null) setCollapsed(saved === "true");
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {}
  }, [collapsed]);

  return (
    <aside
      className={`sticky top-0 hidden h-screen shrink-0 border-r border-border/60 bg-background transition-[width] duration-200 md:block ${
        collapsed ? "w-[72px]" : "w-[280px]"
      }`}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <SidebarContent
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
      />
    </aside>
  );
}

// ============================================================
// MobileSidebar — slide-out drawer for mobile.
// ============================================================
export function MobileSidebar({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={() => onOpenChange(false)} />
      <div className="absolute left-0 top-0 h-full w-[280px] overflow-y-auto bg-background shadow-xl">
        <SidebarContent collapsed={false} onNavigate={() => onOpenChange(false)} />
      </div>
    </div>
  );
}

// ============================================================
// SidebarItem — a single nav item in the sidebar.
// ============================================================
function SidebarItem({
  module: m,
  active,
  collapsed,
  badge,
  onClick,
  label,
}: {
  module: NavModule;
  active: boolean;
  collapsed: boolean;
  badge?: number | null;
  onClick: () => void;
  label: string;
}) {
  const Icon = m.icon;
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      title={collapsed ? label : undefined}
      className={`relative flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      } ${collapsed ? "justify-center" : ""}`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="flex-1 truncate text-left">{label}</span>}
      {!collapsed && badge != null && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-[10px] font-bold text-accent-foreground">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
      {collapsed && badge != null && (
        <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold text-accent-foreground">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
      {m.badge && !collapsed && (
        <span className="rounded bg-primary/15 px-1 py-0.5 text-[9px] font-bold uppercase text-primary">
          {m.badge}
        </span>
      )}
    </button>
  );
}

// ============================================================
// SidebarIconOnly — collapsed-mode icon button for footer items.
// ============================================================
function SidebarIconOnly({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition ${
        danger
          ? "text-destructive hover:bg-destructive/10 hover:text-destructive"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

// ============================================================
// StatRow — a single stat widget in the sidebar.
// ============================================================
function StatRow({
  stat,
  user,
}: {
  stat: { id: string; labelKey: string; label: string; icon: LucideIcon; source: string };
  user: { rating?: number; ratingCount?: number; activeRole?: string } & Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const Icon = stat.icon;

  const { data: earningsData } = useQuery({
    queryKey: ["sidebar-stat-earnings"],
    queryFn: () => api.job.earnings(),
    enabled: stat.source === "earnings",
    refetchInterval: 60000,
  });

  const { data: walletData } = useQuery({
    queryKey: ["sidebar-stat-wallet", user.activeRole],
    queryFn: () => api.wallet.me(),
    enabled: stat.source === "wallet",
    refetchInterval: 60000,
  });

  const value = (() => {
    switch (stat.source) {
      case "earnings":
        if (!earningsData?.ok) return "—";
        return `$${(earningsData.data as any)?.today?.toFixed(2) ?? "0.00"}`;
      case "rating":
        return user.rating ? `${user.rating.toFixed(1)} ★` : "—";
      case "wallet": {
        if (!walletData?.ok) return "—";
        const wallets = (walletData.data as any)?.wallets as Array<{ type: string; balance: number }> | undefined;
        const sender = wallets?.find((w) => w.type === "sender");
        return sender ? `$${sender.balance.toFixed(2)}` : "—";
      }
      case "revenue":
      case "orders":
        return "—";
      default:
        return "—";
    }
  })();

  const label = t(stat.labelKey) !== stat.labelKey ? t(stat.labelKey) : stat.label;

  return (
    <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-2.5 py-1.5">
      <Icon className="h-3.5 w-3.5 text-primary" />
      <span className="flex-1 text-[11px] font-medium text-muted-foreground">{label}</span>
      <span className="text-xs font-bold text-foreground">{value}</span>
    </div>
  );
}
